import _capitalize from "lodash/capitalize";
import _get from "lodash/get";
import _includes from "lodash/includes";
import {
    ACCESSORY_CATEGORIES,
    GA_CATEGORIES as CATEGORIES,
    DELIVERY,
    INSURANCE,
    GA_TEXT_CONSTS as TEXT,
    CategoriesString,
    CUSTOMER_TYPE,
    FINANCING_TYPE,
    CART_ITEM_SUBSCRIPTION_TYPES,
    CART_ITEM_TYPE,
    COMMITMENT_UPFRONT_TYPE,
    Realm,
    REALM,
} from "../../../constants";
import { ICartItem, ICartItemInsurance, InsuranceID } from "../../../models";
import { getFlattenedCartContent, State as CartState } from "../checkout/state/selectors/cartSelectors";
import { getPhoneNumberCheckType, State as PhoneNumberState } from "../checkout/state/selectors/phoneNumberSelectors";
import {
    getSubscriptionFinancingCode,
    getSubscriptionFinancingDuration,
    getSubscriptionFinancingType,
    isSubscriptionBroadband,
    isSubscriptionPrepaid as isSubscriptionPrepaidSelector,
    isTeliaTryggSubscription,
    State as SubscriptionState,
} from "../checkout/state/selectors/subscriptionsSelectors";
import { isGetExistingCustomer, isGetWelcomeOffer, State as NavigationState } from "../navigation/state/selectors";

export type State = CartState & PhoneNumberState & SubscriptionState & NavigationState;
export const getPriceIncVatForType = (state: State, product: ICartItem, realm: Realm): number => {
    // 1) Subscription: monthly * 12 (For b2b its monthly * 24)
    // 2) New number fee: firstInvoice price
    // 3) With bundleId: Need to figure out financing and calculate price thereafter
    // 4) Assume upfront price is correct to use

    const subscriptionDuration = realm === REALM.B2B ? 24 : 12;

    if ((CART_ITEM_SUBSCRIPTION_TYPES as readonly string[]).includes(product.type)) {
        return (
            _get(product, "price.monthly", 0) * subscriptionDuration -
            _get(product, "discount.value.monthly", 0) * subscriptionDuration
        );
    } else if (product.type === CART_ITEM_TYPE.SERVICE) {
        const price = _get(product, "price.monthly", 0) - _get(product, "discount.value.monthly", 0);

        if (_includes(product.id, "DATA_SIM")) {
            return price * 11; //the first month is for free = just 11
        }

        const isInsurance = (cartItem: ICartItem): cartItem is ICartItemInsurance => {
            return _includes(cartItem.id, "INSURANCE");
        };

        // Insurance prices calculation
        if (isInsurance(product)) {
            if (INSURANCE[product.id]) {
                return price * INSURANCE[product.id].MULTIPLIER;
            } else if (_includes(product.id, "INSURANCE")) {
                // if we have missed to add any new (or old) insurance SOCs.
                return price * 12;
            }
        }

        return price;
    } else if (
        (
            [
                CART_ITEM_TYPE.NEW_NUMBER,
                CART_ITEM_TYPE.BREAKAGE_FEE,
                CART_ITEM_TYPE.SWITCH_BREAKAGE_FEE,
                CART_ITEM_TYPE.SWITCH_REMAINING_COST,
            ] as readonly string[]
        ).includes(product.type)
    ) {
        return _get(product, "price.firstInvoice", 0); // TODO: Change fallback price
    }

    if (product.bundleId) {
        const financing = getSubscriptionFinancingType(state, product.bundleId);
        let duration = 24; // Default to switch duration, which is 24 in GA

        if (financing === FINANCING_TYPE.LEASE) {
            const code = getSubscriptionFinancingCode(state, product.bundleId);

            // For lease with code 'now', item will be paid for upfront
            if (code === COMMITMENT_UPFRONT_TYPE) {
                return _get(product, "price.upfront", 0);
            }

            duration = getSubscriptionFinancingDuration(state, product.bundleId);
        }

        return (
            _get(product, "price.monthly", 0) * duration +
            (_get(product, "price.upfront", 0) - _get(product, "discount.value.upfront", 0))
        );
    }

    return _get(product, "price.upfront", 0);
};

export const getAcquisitionOrRetention = (state: State, subscriptionId?: string) => {
    const customerType = getPhoneNumberCheckType(state, subscriptionId);
    switch (customerType) {
        case CUSTOMER_TYPE.NEW:
        case CUSTOMER_TYPE.PORT_IN:
            return TEXT.SALES_TYPE.ACQUISITION;
        case CUSTOMER_TYPE.EXISTING:
            return TEXT.SALES_TYPE.RETENTION;
        default:
            return undefined;
    }
};

export const getCategory = (state: State, product: ICartItem): CategoriesString => {
    let category: CategoriesString;

    const isSubscriptionPrepaid = isSubscriptionPrepaidSelector(state, product.bundleId);
    const isSubscriptionMBB = isSubscriptionBroadband(state, product.bundleId);
    const isSecurityTryggService = isTeliaTryggSubscription(state, product.bundleId);

    switch (product.type) {
        case CART_ITEM_TYPE.SUBSCRIPTION:
        case CART_ITEM_TYPE.SUBSCRIPTION_DRAFT:
            if (isSubscriptionMBB) {
                category = CATEGORIES.SUBSCRIPTION_MBB;
            } else if (isSecurityTryggService) {
                category = CATEGORIES.SUBSCRIPTION_SECURITY_TRYGG;
            } else if (isSubscriptionPrepaid) {
                category = CATEGORIES.SUBSCRIPTION_PREPAID;
            } else {
                category = CATEGORIES.SUBSCRIPTION_POSTPAID;
            }
            break;
        case CART_ITEM_TYPE.ACCESSORY:
            category = ACCESSORY_CATEGORIES(_capitalize((product.subtype || "Annet").toLowerCase()));
            break;
        case CART_ITEM_TYPE.SERVICE:
            category = CATEGORIES.SERVICE_OTHER;

            if (_includes(product.id, "INSURANCE")) {
                category = CATEGORIES.SERVICE_INSURANCE;
            } else if (_includes(product.id, "HBO")) {
                category = CATEGORIES.SERVICE_VAS;
            }

            break;
        case CART_ITEM_TYPE.SPECIALPRODUCT:
            category = CATEGORIES.SERVICE_OTHER;
            break;
        default:
            category = CATEGORIES[product.type as keyof typeof CATEGORIES];
    }

    return category;
};

export const getProductName = (product: ICartItem): string => {
    switch (product.type) {
        case CART_ITEM_TYPE.HANDSET:
        case CART_ITEM_TYPE.HANDSET_PREOWNED:
        case CART_ITEM_TYPE.MODEM:
        case CART_ITEM_TYPE.WEARABLE:
        case CART_ITEM_TYPE.CONNECTED_DEVICE:
        case CART_ITEM_TYPE.ACCESSORY: {
            const sizeMatch = product.model.match(new RegExp(" \\d+\\s?GB"));
            if (sizeMatch) {
                return product.model.substring(0, sizeMatch.index);
            }
            return product.model;
        }
        case CART_ITEM_TYPE.TABLET: {
            const regexMatch = product.model.match(new RegExp("(.+)\\((.+), \\d+\\s?GB"));
            if (regexMatch?.length === 3) {
                return regexMatch[1] + regexMatch[2].replace(", ", " ");
            }
            return product.model;
        }
        case CART_ITEM_TYPE.SERVICE:
            // Insurance name calculation
            if (INSURANCE[product.id as InsuranceID]) {
                return INSURANCE[product.id as InsuranceID].NAME;
            }
            return product.name;
        case CART_ITEM_TYPE.DELIVERY:
            if (DELIVERY[product.id]) {
                return DELIVERY[product.id];
            }
            return product.name;
        default: {
            let productName = product.name;
            const positionOfGB = productName.indexOf(" GB "); // get rid of text after GB: "SMART 20 GB kr 529 med telefon og 12 md. avtaletid"
            if (positionOfGB > -1) productName = productName.substring(0, positionOfGB + 3);
            return productName;
        }
    }
};

export const getProductId = (product: ICartItem) => {
    switch (product.type) {
        case CART_ITEM_TYPE.SUBSCRIPTION:
        case CART_ITEM_TYPE.SUBSCRIPTION_DRAFT:
        case CART_ITEM_TYPE.HANDSET:
        case CART_ITEM_TYPE.HANDSET_PREOWNED:
        case CART_ITEM_TYPE.MODEM:
        case CART_ITEM_TYPE.TABLET:
        case CART_ITEM_TYPE.WEARABLE:
        case CART_ITEM_TYPE.CONNECTED_DEVICE:
        case CART_ITEM_TYPE.ACCESSORY:
        case CART_ITEM_TYPE.SERVICE:
            return product.id;
        default:
            return product.type.toUpperCase().replace(" ", "_");
    }
};

export const getNumItems = (state: State): number => {
    return getFlattenedCartContent(state).filter(
        (product: ICartItem) =>
            !product.name.startsWith("Beholder dagens abonnement") &&
            !product.name.startsWith("Bytter fra") &&
            !product.name.startsWith("Eksisterende Telia"),
    ).length;
};

export const getConvergence = (state: State): string | undefined => {
    const params = new URLSearchParams(window.location.search);
    const existingOffer = params.get("ts") === "true" || isGetExistingCustomer(state);
    const welcomeOffer = params.get("wo") === "true" || isGetWelcomeOffer(state);

    if (existingOffer) {
        return "ts (Telia Samlet)";
    }
    if (welcomeOffer) {
        return "wo (Welcome Offer)";
    }
};
